import { useState, useCallback, useRef, useEffect, useMemo, Suspense } from 'react';
import { FileText, Edit3, X, Check, Loader2, Clock, FileType } from 'lucide-react';
import clsx from 'clsx';
import type { Document } from '@/types';
import { TipTapEditorLazy } from '@/components/editor/TipTapEditorLazy';
import { EditorLoadingFallback } from '@/components/editor/EditorLoadingFallback';
import { updateDocument } from '@/db/projects';
import { useAppStore } from '@/store/appStore';
import { handleError } from '@/utils/errorHandler';

interface DocumentViewProps {
  document: Document;
  onUpdate?: (id: string, updates: Partial<Document>) => Promise<void>;
  onClose?: () => void;
}

// Helper to count words in HTML content
function countWords(html: unknown): number {
  // Handle null, undefined, or non-string values
  if (!html || typeof html !== 'string') return 0;
  const text = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text ? text.split(' ').length : 0;
}

// Helper to estimate reading time
function getReadingTime(wordCount: number): string {
  const minutes = Math.ceil(wordCount / 200);
  return minutes < 1 ? '< 1 min' : `${minutes} min`;
}

export function DocumentView({ document, onUpdate, onClose }: DocumentViewProps) {
  const updateCurrentItem = useAppStore((state) => state.updateCurrentItem);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [title, setTitle] = useState(document.name || '');
  const [content, setContent] = useState(typeof document.content === 'string' ? document.content : '');
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const hasUnsavedChangesRef = useRef(false);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChangesRef.current) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Calculate document stats
  const stats = useMemo(() => {
    const wordCount = countWords(content);
    return {
      words: wordCount,
      readingTime: getReadingTime(wordCount),
    };
  }, [content]);

  // Sync with prop changes (e.g., when document is updated externally)
  useEffect(() => {
    setTitle(document.name || '');
    setContent(typeof document.content === 'string' ? document.content : '');
  }, [document.name, document.content]);

  // Focus title input when editing
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  // Track content changes
  const handleContentChange = useCallback((newContent: string) => {
    hasUnsavedChangesRef.current = true;
    setContent(newContent);
  }, []);

  // Auto-save content handler
  const handleContentSave = useCallback(
    async (newContent: string) => {
      setIsSaving(true);
      try {
        const updated = await updateDocument(document.id, { content: newContent });
        if (updated) {
          // Update local state
          setContent(newContent);
          // Update store if item is current
          updateCurrentItem({ content: newContent });
          if (onUpdate) {
            await onUpdate(document.id, { content: newContent });
          }
        }
        hasUnsavedChangesRef.current = false;
        setLastSaved(new Date());
      } catch (error) {
        handleError(error, { level: 'error', message: 'Error saving document' });
      } finally {
        setIsSaving(false);
      }
    },
    [document.id, onUpdate, updateCurrentItem]
  );

  // Save title handler
  const handleTitleSave = useCallback(async () => {
    if (!title.trim()) {
      setTitle(document.name);
      setIsEditingTitle(false);
      return;
    }

    if (title !== document.name) {
      setIsSaving(true);
      try {
        const updated = await updateDocument(document.id, { name: title });
        if (updated) {
          // Update local state
          setTitle(title);
          // Update store if item is current
          updateCurrentItem({ name: title });
          if (onUpdate) {
            await onUpdate(document.id, { name: title });
          }
        }
        setLastSaved(new Date());
      } catch (error) {
        handleError(error, { level: 'error', message: 'Errore nel salvataggio del titolo del documento' });
        setTitle(document.name);
      } finally {
        setIsSaving(false);
      }
    }
    setIsEditingTitle(false);
  }, [document.id, document.name, title, onUpdate, updateCurrentItem]);

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTitleSave();
    } else if (e.key === 'Escape') {
      setTitle(document.name);
      setIsEditingTitle(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-surface-primary">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-primary bg-surface-secondary">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <FileText className="w-5 h-5 text-accent-blue shrink-0" />

          {isEditingTitle ? (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <input
                ref={titleInputRef}
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={handleTitleKeyDown}
                onBlur={handleTitleSave}
                className={clsx(
                  'flex-1 min-w-0 px-2 py-1 text-lg font-semibold rounded',
                  'bg-surface-primary border border-border-primary text-text-primary',
                  'focus:outline-none focus:ring-2 focus:ring-accent-violet focus:border-transparent'
                )}
              />
              <button
                onClick={handleTitleSave}
                className="p-1 rounded hover:bg-surface-hover text-accent-emerald"
              >
                <Check className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsEditingTitle(true)}
              className="group flex items-center gap-2 min-w-0"
            >
              <h1 className="text-lg font-semibold text-text-primary truncate">{document.name}</h1>
              <Edit3 className="w-3.5 h-3.5 text-text-quaternary opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-4">
          {/* Document stats */}
          <div className="hidden sm:flex items-center gap-3 text-xs text-text-quaternary border-r border-border-primary pr-4">
            <span className="flex items-center gap-1">
              <FileType className="w-3 h-3" />
              {stats.words} parole
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {stats.readingTime}
            </span>
          </div>

          {/* Save indicator */}
          <div className="flex items-center gap-2 text-xs text-text-quaternary">
            {isSaving ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                <span className="hidden sm:inline">Salvataggio...</span>
              </>
            ) : lastSaved ? (
              <span className="hidden sm:inline">
                Salvato alle{' '}
                {lastSaved.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
              </span>
            ) : null}
          </div>

          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-surface-hover transition-colors text-text-quaternary hover:text-text-secondary"
              title="Chiudi documento"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Editor Content */}
      <div className="flex-1 overflow-auto">
        <Suspense fallback={<EditorLoadingFallback className="h-full min-h-[300px] border-0 rounded-none" />}>
          <TipTapEditorLazy
            content={document.content}
            onChange={handleContentChange}
            onSave={handleContentSave}
            placeholder="Inizia a scrivere..."
            autoSaveDelay={1500}
            className="h-full min-h-[300px] border-0 rounded-none"
          />
        </Suspense>
      </div>
    </div>
  );
}
